import { createSlice } from "@reduxjs/toolkit";
import { IUser } from "../pointOfSale/pointOfSale.typings";

type StateLoading = {
	started?: boolean;
	loading: boolean;
	success: boolean;
	hasError: boolean;
	errorMessage: "";
};

const userSlice = createSlice({
	name: "USER",
	initialState: {
		userRoles: [] as string[],
		isLoading: false,
		error: "",
		userGroupsState: {
			fetchStateLoading: {} as StateLoading,
			groups: [] as string[],
		},
		userDiscountsState: {
			fetchStateLoading: {} as StateLoading,
			canApplyDiscounts: false,
		},
		userAuthenticateRoleState: {
			fetchStateLoading: {} as StateLoading,
			lastAuthenticatedUser: {} as IUser,
		},
	},
	reducers: {
		fetchUserRolesStart: (state) => {
			return {
				...state,
				isLoading: true,
				error: "",
			};
		},
		fetchUserRolesSuccess: (state, { payload }) => {
			return {
				...state,
				userRoles: payload.data,
				isLoading: false,
				error: "",
			};
		},
		fetchUserRolesFailure: (state, { payload }) => {
			return {
				...state,
				isLoading: false,
				error: payload,
			};
		},
		fetchUserGroupsStart: (state) => {
			return {
				...state,
				userGroupsState: {
					...state.userGroupsState,
					fetchStateLoading: {
						started: true,
						success: false,
						loading: true,
						errorMessage: "",
						hasError: false,
					},
				},
			};
		},
		fetchUserGroupsSuccess: (state, { payload }) => {
			return {
				...state,
				userGroupsState: {
					...state.userGroupsState,
					fetchStateLoading: {
						...state.userGroupsState.fetchStateLoading,
						success: true,
						loading: false,
					},
					groups: payload.data,
				},
			};
		},
		fetchUserGroupsFailure: (state, { payload }) => {
			return {
				...state,
				userGroupsState: {
					...state.userGroupsState,
					fetchStateLoading: {
						...state.userGroupsState.fetchStateLoading,
						success: false,
						loading: false,
						hasError: true,
						errorMessage: payload,
					},
				},
			};
		},
		fetchUserDiscountsStart: (state) => {
			return {
				...state,
				userDiscountsState: {
					...state.userDiscountsState,
					fetchStateLoading: {
						started: true,
						success: false,
						loading: true,
						errorMessage: "",
						hasError: false,
					},
				},
			};
		},
		fetchUserDiscountsCompleted: (state, { payload }) => {
			return {
				...state,
				userDiscountsState: {
					...state.userDiscountsState,
					fetchStateLoading: {
						...state.userDiscountsState.fetchStateLoading,
						success: true,
						loading: false,
					},
					canApplyDiscounts: payload.data,
				},
			};
		},
		fetchUserDiscountsFailure: (state, { payload }) => {
			return {
				...state,
				userDiscountsState: {
					...state.userDiscountsState,
					fetchStateLoading: {
						...state.userDiscountsState.fetchStateLoading,
						success: false,
						loading: false,
						hasError: true,
						errorMessage: payload,
					},
				},
			};
		},
		authenticateUserRoleStart: (state, { payload }) => {
			return {
				...state,
				userAuthenticateRoleState: {
					...state.userAuthenticateRoleState,
					fetchStateLoading: {
						started: true,
						success: false,
						loading: true,
						errorMessage: "",
						hasError: false,
					},
				},
			};
		},
		authenticateUserRoleCompleted: (state, { payload }) => {
			return {
				...state,
				userAuthenticateRoleState: {
					...state.userAuthenticateRoleState,
					fetchStateLoading: {
						started: true,
						success: true,
						loading: true,
						errorMessage: "",
						hasError: false,
					},
					lastAuthenticatedUser: payload.data,
				},
			};
		},
		authenticateUserRoleFailure: (state, { payload }) => {
			return {
				...state,
				userAuthenticateRoleState: {
					...state.userAuthenticateRoleState,
					fetchStateLoading: {
						...state.userAuthenticateRoleState.fetchStateLoading,
						success: false,
						loading: false,
						hasError: true,
						errorMessage: payload.error,
					},
				},
			};
		},
		resetAuthenticatedUserRole: (state) => {
			return {
				...state,
				userAuthenticateRoleState: {
					...state.userAuthenticateRoleState,
					fetchStateLoading: {
						started: false,
						success: false,
						loading: false,
						hasError: false,
						errorMessage: "",
					},
					lastAuthenticatedUser: {} as IUser,
				},
			};
		},
		resetAuthenticationUserRoleState: (state) => {
			return {
				...state,
				userAuthenticateRoleState: {
					...state.userAuthenticateRoleState,
					fetchStateLoading: {
						started: false,
						success: false,
						loading: false,
						hasError: false,
						errorMessage: "",
					},
				},
			};
		},
	},
});

export const {
	fetchUserRolesStart,
	fetchUserRolesSuccess,
	fetchUserRolesFailure,
	fetchUserGroupsStart,
	fetchUserGroupsSuccess,
	fetchUserGroupsFailure,
	fetchUserDiscountsStart,
	fetchUserDiscountsCompleted,
	fetchUserDiscountsFailure,
	authenticateUserRoleStart,
	authenticateUserRoleCompleted,
	authenticateUserRoleFailure,
	resetAuthenticatedUserRole,
	resetAuthenticationUserRoleState,
} = userSlice.actions;

export default userSlice.reducer;
